import {
  Description,
  DirectionsCar,
  EmojiObjectsOutlined,
  GavelRounded,
  Groups,
  Headphones,
  Medication,
  Person,
  RecordVoiceOver,
  WaterDrop,
} from '@mui/icons-material';
import { type ReactElement } from 'react';

import {
  COURT_CASES_V2_ROUTES,
  INTELLECTUAL_PROPERTY_ROUTES,
  type LegalIntelSection,
} from '../../CourtCases/constants';
import { type ExploreProductLinkItem } from '../components/ExploreByProductCategory';

// ===================== ToolsSection =====================

export type ToolLink = {
  label: string;
  path: string;
  legalIntelSection: LegalIntelSection;
  icon: ReactElement;
};

export const TOOL_LINKS: ToolLink[] = [
  {
    label: 'Court Cases',
    path: COURT_CASES_V2_ROUTES.CASES,
    legalIntelSection: 'legal-research',
    icon: <GavelRounded fontSize="inherit" />,
  },
  {
    label: 'Class Actions',
    path: COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUITS,
    legalIntelSection: 'class-actions-recalls',
    icon: <Groups fontSize="inherit" />,
  },
  {
    label: 'Recalls',
    path: COURT_CASES_V2_ROUTES.RECALLS,
    legalIntelSection: 'class-actions-recalls',
    icon: <WaterDrop fontSize="inherit" />,
  },
  {
    label: 'Opinions',
    path: COURT_CASES_V2_ROUTES.OPINIONS,
    legalIntelSection: 'legal-research',
    icon: <Description fontSize="inherit" />,
  },
  {
    label: 'Judge Search',
    path: COURT_CASES_V2_ROUTES.JUDGE_SEARCH,
    legalIntelSection: 'legal-research',
    icon: <Person fontSize="inherit" />,
  },
  {
    label: 'Oral Arguments',
    path: COURT_CASES_V2_ROUTES.ORAL_ARGUMENTS,
    legalIntelSection: 'legal-research',
    icon: <RecordVoiceOver fontSize="inherit" />,
  },
  {
    label: 'Patent Search',
    path: INTELLECTUAL_PROPERTY_ROUTES.BASE,
    legalIntelSection: 'intellectual-property',
    icon: <EmojiObjectsOutlined fontSize="inherit" />,
  },
];

// ===================== ExploreByProductSection =====================

export type ExploreProductBlockConfig = {
  id: string;
  illustration: 'top' | 'bottom' | 'intellectual-property';
  sectionTitle: string;
  legalIntelSection: LegalIntelSection;
  intro: string;
  navAriaLabel: string;
  links: (Omit<ExploreProductLinkItem, 'to'> & { path: string })[];
  imageAlt: string;
};

export const EXPLORE_BY_PRODUCT_BLOCKS: ExploreProductBlockConfig[] = [
  {
    id: 'legal-research',
    sectionTitle: 'Legal Research',
    legalIntelSection: 'legal-research',
    intro:
      'Search court cases and court filings to investigate litigation, legal history, and ongoing proceedings across jurisdictions.',
    navAriaLabel: 'Legal research products',
    illustration: 'top',
    imageAlt: 'Sample court case overview',
    links: [
      {
        title: 'Court Cases',
        description:
          'Search federal and state court opinions to review rulings, legal reasoning, and case outcomes.',
        path: COURT_CASES_V2_ROUTES.CASES,
        icon: <GavelRounded fontSize="inherit" />,
      },
      {
        title: 'Opinions',
        description:
          'Research judges, their case history, and prior rulings across jurisdictions.',
        path: COURT_CASES_V2_ROUTES.OPINIONS,
        icon: <Person fontSize="inherit" />,
      },
      {
        title: 'Oral Arguments',
        description:
          'Explore court hearing recordings and transcripts to understand arguments presented before judges.',
        path: COURT_CASES_V2_ROUTES.ORAL_ARGUMENTS,
        icon: <RecordVoiceOver fontSize="inherit" />,
      },
    ],
  },
  {
    id: 'class-actions-recalls',
    sectionTitle: 'Class Actions & Recalls',
    legalIntelSection: 'class-actions-recalls',
    intro:
      'Search class action settlement and recall notices to track major litigation, settlements, and consumer safety alerts across vehicles, food, drugs, and household products.',
    navAriaLabel: 'Class actions and recalls products',
    illustration: 'bottom',
    imageAlt: 'Sample class action overview',
    links: [
      {
        title: 'Class Action Settlements',
        description:
          'Access hundreds of millions of class action settlement records.',
        path: COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUITS,
        icon: <Groups fontSize="inherit" />,
      },
      {
        title: 'Vehicle Recalls',
        description:
          'Search safety recalls and manufacturer notices for vehicles and automotive components.',
        path: COURT_CASES_V2_ROUTES.RECALLS,
        icon: <DirectionsCar fontSize="inherit" />,
      },
      {
        title: 'Food, Drug & Medical Device Recalls',
        description:
          'Find FDA and USDA recalls, safety alerts, and enforcement actions involving food and pharmaceuticals.',
        path: `${COURT_CASES_V2_ROUTES.RECALLS}?recallPill=Food`,
        icon: <Medication fontSize="inherit" />,
      },
      {
        title: 'Consumer Product Recalls',
        description:
          'Search safety recalls and regulatory notices for consumer goods and household products.',
        path: `${COURT_CASES_V2_ROUTES.RECALLS}?recallPill=Consumer+Products`,
        icon: <Headphones fontSize="inherit" />,
      },
    ],
  },
  {
    id: 'intellectual-property',
    sectionTitle: 'Intellectual Property',
    legalIntelSection: 'intellectual-property',
    intro:
      'Explore patents and copyright registrations to research ownership, filings, and protections for creative and technical works.',
    navAriaLabel: 'Intellectual property products',
    illustration: 'intellectual-property',
    imageAlt: 'Intellectual property illustration',
    links: [
      {
        title: 'Patent Search',
        description:
          'Explore patent filings, inventors, and technical disclosures across industries.',
        path: INTELLECTUAL_PROPERTY_ROUTES.BASE,
        icon: <EmojiObjectsOutlined fontSize="inherit" />,
      },
    ],
  },
];
